import classNames from 'classnames';
import React, {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  HTMLAttributes,
  Ref,
  useEffect,
  useId,
  useState,
} from 'react';

import styles from './InlineInput.module.css';
import { InputType } from '../Input/Input';

export type InputStatus = 'default' | 'error' | 'info' | 'success' | 'alert';

export interface InlineInputProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * Whether the input field can be focused with a single click
   * or if it requires a double click to focus.
   * @default 'single'
   */
  clickMode?: 'single' | 'double' | undefined;
  /**
   * Whether or not the input field is disabeld.
   */
  disabled?: boolean;
  /**
   * The ID for the input element. It is added to the input element
   * and not to the container. Should be used for accessibility linking.
   */
  id?: string;
  /**
   * Optional CSS class for the input element.
   * Use with care: it must not change layouting styles, but it
   * can be used to e.g. colorize the :placeholder pseudo-element.
   */
  inputClassName?: string;
  /**
   * The unique ID to be provided for the input DOM element.
   * It will also be used for accessibility linking.
   * If not provided, one is generated.
   */
  inputId?: string | undefined | null;
  /**
   * The label text for the input. Not visible, but used for accessibility.
   */
  label: string;
  /**
   * Maximum allowed length of the input with HTML5 validation.
   */
  maxLength?: number;
  /**
   * Minimum allowed length of the input with HTML5 validation.
   */
  minLength?: number;
  /**
   * A name of the form input element.
   */
  name?: string;
  /**
   * The event handler when the input field looses focus.
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * The event handler when the value on the input field changes.
   * NOTE: If you are only interested in the new value, you can use `onValueChange` instead!
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * The event handler when the input field is focused.
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /**
   * The event handler for key down events on the input field.
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * The event handler for key up events on the input field.
   */
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * The event handler when the value on the input field changes.
   * This one takes the new value as the first parameter and the event as the second parameter.
   * It's easier to use than `onChange`.
   */
  onValueChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Required regexp pattern for valid values with HTML5 validation.
   */
  pattern?: string;
  /**
   * Placeholder text to show when the value is empty.
   */
  placeholder?: string;
  /**
   * Whether or not the input field is read only and cannot be edited.
   */
  readOnly?: boolean;
  /**
   * Whether or not to make the input required with HTML5 validation.
   */
  required?: boolean;
  /**
   * Type of the input field. Defaults to `text`.
   */
  type?: InputType;
  /**
   * String value shown in the input field (for controlled state management).
   */
  value: string | undefined;
}
/**
 * This special input works semantically and accessibility-wise like a regular input,
 * except that it renders a "inline-editable" text, similar to "contenteditable" HTML attribute,
 * but it never allows text formatting, HTML content or linebreaks.
 *
 * Technically it's just a regular `<input>` with auto-resizing functionality.
 */
export const InlineInput = React.forwardRef(
  (props: InlineInputProps, ref: Ref<HTMLInputElement>) => {
    const {
      id,
      value,
      label,
      placeholder,
      className,
      inputClassName,
      style,
      required,
      disabled,
      clickMode = 'single',
      onChange,
      onValueChange,
      ...inputProps
    } = props;
    const generatedInputId = useId();
    const inputId = id ?? generatedInputId;
    const onInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      onValueChange?.(event.currentTarget.value, event);

      onChange?.(event);
    };
    const [showClickMask, setShowClickMask] = useState(true);

    // Ensure that the first click on the click mask hides it for a short period for time
    // allowing the second click to be registered as a click on the input field.
    useEffect(() => {
      if (showClickMask) {
        return undefined;
      }
      const timeout = setTimeout(() => {
        setShowClickMask(true);
      }, 500);

      return () => clearTimeout(timeout);
    }, [showClickMask]);

    return (
      <div
        className={classNames(styles.field, disabled && styles.disabled, className)}
        style={style}
      >
        <input
          aria-label={label}
          className={classNames(styles.input, inputClassName)}
          disabled={disabled}
          id={inputId}
          onChange={onInputChange}
          placeholder={placeholder}
          ref={ref}
          required={required}
          type="text"
          value={value}
          {...inputProps}
        />
        <span
          aria-hidden
          className={styles['value-filler']}
        >
          {value}
        </span>
        {!value && (
          <span
            aria-hidden
            className={styles['placeholder-filler']}
          >
            {placeholder}
          </span>
        )}
        {clickMode === 'double' && showClickMask && !disabled && (
          // NOTE: Purposefully ignoring accessibility for this element, as for accessibility the
          // input field can be navigated to normally. This element only exists for regular non-assisted clicks.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className={styles['click-mask']}
            onClick={() => setShowClickMask(false)}
          />
        )}
      </div>
    );
  },
);
